"use client"

import { useForm } from "@inertiajs/react"
import { useReducedMotion } from "motion/react"
import type { FormEvent } from "react"
import { useEffect, useId, useRef, useState } from "react"
import { toast } from "sonner"

import { useUrlPrefill } from "@/pages/tracks/_hooks/use-url-prefill"
import { collabSuggestionsPath } from "@/routes"
import type { Brief, TrackPrompt } from "@/types"

import { CollabCompletePanel } from "./collab-complete-panel"
import { ComposerForm, type SuggestionDraft } from "./composer-form"
import { ConversationCardDeck } from "./conversation-card-deck"
import type { ComposerPreview } from "./unfurl-preview"

export interface DeckState {
  index: number
  direction: number
}

interface SuggestionComposerProps {
  brief: Brief
  /** Suggestable prompts only (status.can_suggest), derived by the page. */
  prompts: TrackPrompt[]
  deck: DeckState
  onNavigate: (direction: 1 | -1) => void
}

interface SuggestionFormShape {
  suggestion: SuggestionDraft
  [key: string]: SuggestionDraft | string
}

// How long the affirmation chip holds the card before the deck deals to
// the next open prompt.
const AFFIRM_HOLD_MS = 900

// The one tactile place: the deck asks, the form answers. A single draft
// survives card cycling — "I have a link, which card does it answer?" must
// never cost someone their half-written why.
export function SuggestionComposer({
  brief,
  prompts,
  deck,
  onNavigate,
}: SuggestionComposerProps) {
  const reduceMotion = useReducedMotion()
  const headingId = useId()
  const cardRegionId = useId()
  const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [affirm, setAffirm] = useState(false)
  const [announcementPrefix, setAnnouncementPrefix] = useState<string>()
  // The success prefix should be announced exactly once — with the card
  // it advances onto — then dropped from subsequent navigations.
  const prefixFreshRef = useRef(false)

  useEffect(() => {
    if (prefixFreshRef.current) {
      prefixFreshRef.current = false
      return
    }
    setAnnouncementPrefix(undefined)
  }, [deck.index])

  const { data, setData, post, processing, errors, transform } =
    useForm<SuggestionFormShape>({
      suggestion: { url: "", notes: "", name: "", email: "", title: "" },
    })

  // Display-only projection of the unfurl (source/type/thumbnail/duration are
  // server-authoritative); the editable title lives in the form draft.
  const [preview, setPreview] = useState<ComposerPreview | null>(null)

  // Live client-side unfurl: fires the moment a plausible URL lands, so the
  // preview and the "why" field fill the network wait. Submit still POSTs the
  // raw URL — the server re-resolves through its 12h cache (a hit) — so this
  // is preview + the editable title override, never a second fetch on save.
  const { loading, failed } = useUrlPrefill({
    url: data.suggestion.url,
    onResult: (result) => {
      setPreview({
        status: result.status,
        contentBodyType: result.content_body_type,
        source: result.source,
        thumbnailUrl: result.fields.thumbnail_url ?? null,
        durationSeconds: result.fields.duration_seconds ?? null,
      })
      // Seed the editable title from the fresh unfurl. onResult fires once per
      // new URL, so this never clobbers a title the contributor has edited.
      if (result.fields.title != null) {
        setData("suggestion", {
          ...data.suggestion,
          title: result.fields.title,
        })
      }
    },
  })

  useEffect(() => {
    return () => {
      if (advanceTimer.current) clearTimeout(advanceTimer.current)
    }
  }, [])

  const currentPrompt = prompts[Math.min(deck.index, prompts.length - 1)]

  const updateField = <K extends keyof SuggestionDraft>(
    field: K,
    value: SuggestionDraft[K],
  ) => {
    setData("suggestion", { ...data.suggestion, [field]: value })
    // Clearing the URL resets the slip — a re-paste starts from a clean
    // skeleton rather than flashing the last link's details.
    if (field === "url" && String(value).trim() === "") setPreview(null)
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (!currentPrompt) return
    const promptTitle = currentPrompt.title

    // The target prompt is injected at submit time so cycling the deck
    // mid-typing never mutates the draft.
    transform((formData) => ({
      suggestion: { ...formData.suggestion, track_prompt_id: currentPrompt.id },
    }))

    post(collabSuggestionsPath(brief.slug), {
      preserveScroll: true,
      onSuccess: () => {
        // Keep name/email: on a guest's validation round-trip the server
        // hasn't consumed them yet, and fresh props retire the fields once
        // the guest cookie lands.
        setData("suggestion", {
          ...data.suggestion,
          url: "",
          notes: "",
          title: "",
        })
        setPreview(null)
        setAffirm(true)
        setAnnouncementPrefix(`Your answer was added to "${promptTitle}".`)
        prefixFreshRef.current = true
        toast("It'll appear below once a curator's seen it.")
        advanceTimer.current = setTimeout(
          () => {
            setAffirm(false)
            if (prompts.length > 1) onNavigate(1)
          },
          reduceMotion ? 0 : AFFIRM_HOLD_MS,
        )
      },
    })
  }

  return (
    <section
      id="track-prompts"
      data-track-prompts
      aria-labelledby={headingId}
      className="bg-surface-grouped rounded-3xl p-5 sm:p-8 lg:p-10"
    >
      <p className="text-tertiary text-xs font-medium tracking-wider uppercase">
        Your turn
      </p>
      <h2 id={headingId} className="text-primary mt-1 text-xl font-semibold">
        {prompts.length > 0 ? "Answer a card" : "Every card is answered"}
      </h2>

      {prompts.length === 0 || !currentPrompt ? (
        <CollabCompletePanel
          participationState={brief.participation_state}
          canonicalUrl={brief.canonical_url}
        />
      ) : (
        <div className="mt-8 grid items-center gap-8 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)] lg:gap-12">
          <div id={cardRegionId}>
            <ConversationCardDeck
              prompts={prompts}
              index={deck.index}
              direction={deck.direction}
              onNavigate={onNavigate}
              affirm={affirm}
              announcementPrefix={announcementPrefix}
            />
          </div>
          <ComposerForm
            draft={data.suggestion}
            errors={errors as Partial<Record<string, string>>}
            processing={processing}
            requiresGuestIdentity={brief.requires_guest_identity}
            onFieldChange={updateField}
            onSubmit={handleSubmit}
            describedById={cardRegionId}
            loading={loading}
            failed={failed}
            preview={preview}
            reduceMotion={!!reduceMotion}
          />
        </div>
      )}
    </section>
  )
}
